// Runtime live-config validation + secret refusal for Guard Web.
//
// The host injects `window.__SECOPS_LIVE_CONFIG__` BEFORE the app bundle runs (in dev via the
// gitignored public/live-config.local.js; in production via the host). The publishable key is
// PUBLIC (anon-style) but is supplied only at RUNTIME and is never committed.
//
// This module is the CONFIG GATE. It validates the injected shape and, CRITICALLY, REFUSES a key
// that looks like a service-role / secret / JWT / bearer / raw chk_ token / 64-hex hash - only a
// publishable/anon-style key is accepted. The refusal `reason` is always SAFE: it never echoes the
// key value. A refused config can never be turned into a live supabase client, so a secret can
// never reach the browser data path.
//
// Ported from apps/console/src/lib/live-config.ts (same gate, so the surfaces refuse identically),
// trimmed of the map-only fields the console needs and the guard app does not. Candidate for a
// shared @secops package later (Codex's lane) - three copies now (apps/web, console, guard-web).

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DEFAULT_REFRESH_MS = 30000;
const DEFAULT_DISPLAY_TIME_ZONE = "America/New_York";

/** The validated, browser-safe runtime config. accountId is normalized to lowercase. */
export interface LiveConfig {
  supabaseUrl: string;
  supabasePublishableKey: string;
  accountId: string;
  accountName: string;
  refreshMs: number;
  /**
   * IANA timezone all displayed timestamps render in (display-only; stored values stay UTC).
   * Defaults to "America/New_York" (Eastern; DST-aware). A non-IANA value falls back to default.
   */
  displayTimeZone: string;
  /**
   * Optional PostHog project key (publishable phc_… — browser-class by design, but still runtime-
   * injected only, never committed) + ingest host. Both null = analytics fully off (no-op, no SDK
   * load). A host without a key is meaningless and ignored.
   */
  posthogKey: string | null;
  posthogHost: string | null;
  /**
   * Optional Sentry browser DSN (public client key). Null = error reporting off (no SDK load).
   * Runtime-injected only; present-but-invalid fails the whole config loudly.
   */
  sentryDsn: string | null;
  /** Optional Sentry environment tag when DSN is set (defaults to production). */
  sentryEnvironment: string | null;
}

/** Discriminated result; on failure `reason` is SAFE (never a value). GW-CONFIG-GATE: `problems`
 *  carries EVERY missing/invalid key message (names only) so the boot screen can show operators
 *  the complete fix-list in one look instead of one error per deploy attempt. */
export type ParseLiveConfigResult =
  | { ok: true; config: LiveConfig }
  | { ok: false; reason: string; problems?: string[] };

/**
 * Validate `window.__SECOPS_LIVE_CONFIG__`.
 *
 * Returns { ok: true, config } only when every field is valid AND the publishable key does not look
 * like a service-role / secret / JWT / bearer / raw chk_ token / 64-hex hash. The failure `reason`
 * is a fixed, safe sentence - it never includes the key value.
 */
export function parseLiveConfig(raw: unknown): ParseLiveConfigResult {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    // Nothing injected at all (the live-config script never ran) - still give operators the
    // COMPLETE required-key list by validating an empty object, headline first.
    const empty = parseLiveConfig({});
    const detail = empty.ok ? [] : (empty.problems ?? []);
    return {
      ok: false,
      reason: "Live config is missing or not an object.",
      problems: ["Live config is missing or not an object.", ...detail],
    };
  }
  const source = raw as Record<string, unknown>;
  const problems: string[] = [];

  const url = parseHttpsUrl(source.supabaseUrl);
  if (url === null) {
    problems.push("supabaseUrl must be an https:// URL.");
  }

  const rawKey = source.supabasePublishableKey;
  let key: string | null = null;
  if (typeof rawKey !== "string" || rawKey.trim().length === 0) {
    problems.push("supabasePublishableKey must be a non-empty string.");
  } else if (looksLikeSecret(rawKey.trim())) {
    // SAFE reason - describe the rule, never the value.
    problems.push(
      "supabasePublishableKey was refused: it looks like a service-role/secret/JWT/bearer/raw-token value. Only a publishable (anon) key may be used in the browser.",
    );
  } else {
    key = rawKey.trim();
  }

  let accountId: string | null = null;
  if (typeof source.accountId !== "string" || !UUID_PATTERN.test(source.accountId.trim())) {
    problems.push("accountId must be a UUID.");
  } else {
    accountId = source.accountId.trim().toLowerCase();
  }

  let accountName: string | null = null;
  if (typeof source.accountName !== "string" || source.accountName.trim().length === 0) {
    problems.push("accountName must be a non-empty string.");
  } else {
    accountName = source.accountName.trim();
  }

  const refreshMs = parseRefreshMs(source.refreshMs);
  if (refreshMs === null) {
    problems.push("refreshMs must be a positive integer (milliseconds).");
  }

  const displayTimeZone = resolveDisplayTimeZone(
    typeof source.displayTimeZone === "string" ? source.displayTimeZone : undefined,
  );

  const posthog = parsePosthogFields(source.posthogKey, source.posthogHost);
  if (!posthog.ok) {
    problems.push(posthog.reason);
  }

  const sentry = parseSentryFields(source.sentryDsn, source.sentryEnvironment);
  if (!sentry.ok) {
    problems.push(sentry.reason);
  }

  if (
    problems.length > 0 ||
    url === null ||
    key === null ||
    accountId === null ||
    accountName === null ||
    refreshMs === null ||
    !posthog.ok ||
    !sentry.ok
  ) {
    return { ok: false, reason: problems.join(" "), problems };
  }

  return {
    ok: true,
    config: {
      supabaseUrl: url.toString().replace(/\/$/, ""),
      supabasePublishableKey: key,
      accountId,
      accountName,
      refreshMs,
      displayTimeZone,
      posthogKey: posthog.key,
      posthogHost: posthog.host,
      sentryDsn: sentry.dsn,
      sentryEnvironment: sentry.environment,
    },
  };
}

const POSTHOG_KEY_PATTERN = /^phc_[A-Za-z0-9_-]{16,}$/;
const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";

/** Validate the optional analytics fields. Same posture as the supabase key: a present-but-wrong
 *  value fails loudly with a SAFE reason; only a phc_ publishable project key is accepted. */
function parsePosthogFields(
  rawKey: unknown,
  rawHost: unknown,
): { ok: true; key: string | null; host: string | null } | { ok: false; reason: string } {
  if (rawKey === undefined || rawKey === null) {
    // A host without a key configures nothing - ignore it rather than half-enable analytics.
    return { ok: true, key: null, host: null };
  }
  if (typeof rawKey !== "string" || rawKey.trim().length === 0) {
    return { ok: false, reason: "posthogKey must be a non-empty string when present." };
  }
  const key = rawKey.trim();
  if (looksLikeSecret(key) || !POSTHOG_KEY_PATTERN.test(key)) {
    return {
      ok: false,
      reason: "posthogKey was refused: only a publishable phc_ project key may be used in the browser.",
    };
  }
  if (rawHost === undefined || rawHost === null) {
    return { ok: true, key, host: DEFAULT_POSTHOG_HOST };
  }
  const host = parseHttpsUrl(rawHost);
  if (host === null) {
    return { ok: false, reason: "posthogHost must be an https:// URL when present." };
  }
  return { ok: true, key, host: host.origin };
}

const DEFAULT_SENTRY_ENVIRONMENT = "production";
const SENTRY_ENV_PATTERN = /^[a-z][a-z0-9_-]{0,31}$/;

function parseSentryFields(
  rawDsn: unknown,
  rawEnvironment: unknown,
): { ok: true; dsn: string | null; environment: string | null } | { ok: false; reason: string } {
  if (rawDsn === undefined || rawDsn === null) {
    return { ok: true, dsn: null, environment: null };
  }
  if (typeof rawDsn !== "string" || rawDsn.trim().length === 0) {
    return { ok: false, reason: "sentryDsn must be a non-empty string when present." };
  }
  const dsn = rawDsn.trim();
  if (looksLikeSecret(dsn) || !isBrowserSentryDsn(dsn)) {
    return {
      ok: false,
      reason:
        "sentryDsn was refused: only a public https Sentry browser DSN (ingest.sentry.io project) may be used in the browser.",
    };
  }
  if (rawEnvironment === undefined || rawEnvironment === null) {
    return { ok: true, dsn, environment: DEFAULT_SENTRY_ENVIRONMENT };
  }
  if (typeof rawEnvironment !== "string" || rawEnvironment.trim().length === 0) {
    return { ok: false, reason: "sentryEnvironment must be a non-empty string when present." };
  }
  const environment = rawEnvironment.trim().toLowerCase();
  if (!SENTRY_ENV_PATTERN.test(environment)) {
    return {
      ok: false,
      reason: "sentryEnvironment was refused: use a short tag like staging or production.",
    };
  }
  return { ok: true, dsn, environment };
}

/** True when `value` looks like a public browser Sentry DSN. */
export function isBrowserSentryDsn(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") {
    return false;
  }
  if (url.username.length < 8 || url.password.length > 0) {
    return false;
  }
  if (!/^\/\d+\/?$/.test(url.pathname)) {
    return false;
  }
  return /(?:^|\.)ingest(?:\.(?:us|de|eu))?\.sentry\.io$/i.test(url.hostname);
}

/**
 * Last-good config persistence - the offline COLD-start path. The service worker can serve the
 * cached app shell with no network, but the host-injected /live-config script can't load offline,
 * so without this a guard opening the installed app in a dead zone hits "Configuration required".
 * After every successful parse we store the VALIDATED config; on a failed/missing injection we fall
 * back to the stored copy, re-run through parseLiveConfig (the full secret-refusing gate - stored
 * data is never trusted raw). Publishable-key-only by construction: only gate-accepted configs are
 * ever written.
 */
const LAST_GOOD_CONFIG_KEY = "secops.guard.lastConfig";

function persistLastGoodConfig(config: LiveConfig): void {
  try {
    window.localStorage.setItem(LAST_GOOD_CONFIG_KEY, JSON.stringify(config));
  } catch {
    // Storage unavailable (private mode/quota) - online behavior is unaffected.
  }
}

function readLastGoodConfig(): ParseLiveConfigResult | null {
  try {
    const raw = window.localStorage.getItem(LAST_GOOD_CONFIG_KEY);
    if (raw === null) {
      return null;
    }
    const result = parseLiveConfig(JSON.parse(raw));
    return result.ok ? result : null;
  } catch {
    return null;
  }
}

/** Read + validate the host-injected runtime config from `window`. Falls back to the last
 *  gate-accepted config ONLY when nothing was injected at all (offline cold start - the config
 *  script couldn't load). A present-but-invalid injection fails loudly: a misconfigured deploy
 *  must never be silently masked by a stale stored copy. */
export function readRuntimeConfig(): ParseLiveConfigResult {
  if (typeof window === "undefined") {
    return fail("Runtime config is unavailable (no window).");
  }
  const injectedRaw = window.__SECOPS_LIVE_CONFIG__;
  const injected = parseLiveConfig(injectedRaw);
  if (injected.ok) {
    persistLastGoodConfig(injected.config);
    return injected;
  }
  if (injectedRaw === undefined || injectedRaw === null) {
    const stored = readLastGoodConfig();
    if (stored !== null) {
      return stored;
    }
  }
  return injected;
}

export { LAST_GOOD_CONFIG_KEY };

/**
 * Returns true when a candidate key looks like a SECRET rather than a publishable (anon) key.
 * Matches: service_role marker, sb_secret_ prefix, JWT shape (eyJ... with two dots), a bearer
 * prefix, a raw chk_ checkpoint token, or a bare 64-hex hash. Conservative by design: a false
 * refusal is a deploy-config error (safe); a false accept would put a secret in the browser.
 */
export function looksLikeSecret(key: string): boolean {
  const lower = key.toLowerCase();

  if (lower.includes("service_role")) {
    return true;
  }
  if (lower.includes("sb_secret_")) {
    return true;
  }
  if (/^bearer\b/i.test(key) || /\bbearer\s+\S/i.test(key)) {
    return true;
  }
  // JWT: header.payload.signature, header begins with eyJ (base64url of '{"').
  if (/^eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]+$/.test(key)) {
    return true;
  }
  if (/(^|[^A-Za-z0-9_-])eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]+/.test(key)) {
    return true;
  }
  // Raw checkpoint token: chk_ prefix.
  if (/^chk_/i.test(key) || /\bchk_[A-Za-z0-9]/i.test(key)) {
    return true;
  }
  // Bare 64-hex hash (e.g. a token hash): a 64-hex run with no other hex adjacent.
  if (/(^|[^0-9a-f])[0-9a-f]{64}([^0-9a-f]|$)/i.test(key)) {
    return true;
  }
  return false;
}

/** True when `tz` is a valid IANA timezone (constructing the formatter throws otherwise). */
export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Resolve a display timezone: a valid IANA value is kept; anything else => America/New_York. */
export function resolveDisplayTimeZone(value: string | undefined): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length > 0 && isValidTimeZone(trimmed)) {
      return trimmed;
    }
  }
  return DEFAULT_DISPLAY_TIME_ZONE;
}

function parseHttpsUrl(value: unknown): URL | null {
  if (typeof value !== "string" || value.trim().length === 0) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }
  return url.protocol === "https:" ? url : null;
}

function parseRefreshMs(value: unknown): number | null {
  if (value === undefined) {
    return DEFAULT_REFRESH_MS;
  }
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) {
    return null;
  }
  return value;
}

function fail(reason: string): { ok: false; reason: string } {
  return { ok: false, reason };
}
